import { useCallback, useEffect, useState } from 'react';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import useServerStateContext from '@/shared/hooks/useServerStateContext';
import { useCameraDisplayNames } from '@/shared/hooks/useSelectedCameraWithDisplayName';
import { Stack } from '@mui/material';
import { RedCamera } from './icons/RedCamera';
import { CameraIcon } from './icons/CameraIcon';
import { GrayoutCamera } from './icons/GrayedoutCamera';
import Tracking from '@/shared/services/tracking';
import KeyboardCmd from '@/shared/components/KeyboardCmd';
import {
  CameraDegradedStatusBadge,
  CameraStatusTooltip,
  getDisabledCameraTooltip,
} from './CameraStatusTooltip';
import { getAbbreviation } from '@/shared/utils/getAbbreviation';
import { Shuffle } from 'lucide-react';
import useAutoDirector from '@/shared/hooks/websocket/useAutoDirector';
import {
  AUTO_DIRECTOR_CAMERA_LABEL,
  AUTO_DIRECTOR_TRACKING_DISABLED_TOOLTIP,
} from '@/features/clips/types';
import { useCurrentGame } from '@/features/games/context/CurrentGameContext';

// Selector value for the Auto-Director entry; camIndex is never negative.
const AUTO_DIRECTOR_VALUE = -1;

type DisplayMode = 'full' | 'abbreviated' | 'dropdown';

type Props = {
  value: number;
  onChange?: (value: number) => void;
  disabled: boolean;
  error?: boolean;
  trackingOrigin?: string;
  onError: (error: string | undefined) => void;
  displayMode?: DisplayMode;
};

const PlayerCameraFeedSelectionButton = ({
  value,
  onChange,
  disabled,
  trackingOrigin = 'Video Player Toolbar',
  onError,
  displayMode = 'full',
}: Props) => {
  const {
    availableCameras,
    selectCamera,
    selectedCameraId,
    hasErrorSwitchingCamera,
    autoDirectorEnabled,
    autoDirectorAvailable,
    autoDirectorDefaults,
  } = useServerStateContext();
  const { enableAutoDirector, disableAutoDirector } = useAutoDirector();
  const { game } = useCurrentGame();
  // Auto-Director follows the tracked object, so a game with tracking off has
  // nothing for it to follow. The entry stays visible and explains itself
  // rather than disappearing.
  const trackingDisabled = game.enableTracking === false;
  // While auto-director runs the player owns the camera, so the entry is the
  // selection; the live camera still shows through the red icon below.
  const selectorValue = autoDirectorEnabled ? AUTO_DIRECTOR_VALUE : selectedCameraId;

  const setAutoDirector = useCallback(
    (enabled: boolean) => {
      if (enabled) {
        enableAutoDirector(autoDirectorDefaults);
      } else {
        disableAutoDirector();
      }
      Tracking.getInstance().track('Auto-Director', {
        category: 'Playback',
        enabled,
        source: trackingOrigin,
      });
    },
    [disableAutoDirector, enableAutoDirector, autoDirectorDefaults, trackingOrigin],
  );

  const { getCameraDisplayName } = useCameraDisplayNames();

  const [internalValue, setInternalValue] = useState(value);

  const handleSwitchCamera = useCallback(
    (_event: React.MouseEvent<HTMLElement>, newValue: number | null) => {
      if (disabled) return;
      // An exclusive group reports a re-click of the active entry as null:
      // for Auto-Director that means "turn it off"; for a camera it is a no-op.
      if (newValue === null) {
        if (autoDirectorEnabled) setAutoDirector(false);
        return;
      }
      if (newValue === AUTO_DIRECTOR_VALUE) {
        setAutoDirector(true);
        return;
      }
      Tracking.getInstance().track('Switch Camera', {
        camera: newValue,
        category: 'Playback',
      });
      selectCamera(newValue, trackingOrigin);
      setInternalValue(newValue);
    },
    [selectCamera, trackingOrigin, disabled, autoDirectorEnabled, setAutoDirector],
  );

  const handleDropdownChange = useCallback(
    (event: { target: { value: unknown } }) => {
      const newValue = event.target.value as number;
      if (newValue === AUTO_DIRECTOR_VALUE) {
        if (!disabled && !autoDirectorEnabled) setAutoDirector(true);
        return;
      }
      if (newValue !== null && !disabled) {
        Tracking.getInstance().track('Switch Camera', {
          camera: newValue,
          category: 'Playback',
        });
        selectCamera(newValue, trackingOrigin);
        setInternalValue(newValue);
      }
    },
    [selectCamera, trackingOrigin, disabled, autoDirectorEnabled, setAutoDirector],
  );

  const getCameraText = useCallback(
    (camIndex: number): string => {
      const fullName = getCameraDisplayName(camIndex);
      return displayMode === 'abbreviated' ? getAbbreviation(fullName) : fullName;
    },
    [displayMode, getCameraDisplayName],
  );

  useEffect(() => {
    if (selectedCameraId === internalValue) {
      if (onChange) onChange(internalValue);
    }
  }, [selectedCameraId, internalValue]);

  useEffect(() => {
    if (onError) onError(hasErrorSwitchingCamera ? 'Failed to switch Camera' : undefined);
  }, [hasErrorSwitchingCamera]);

  useEffect(() => {
    if (onError) onError(undefined);
  }, []);

  const wrapWithStatusTooltip = (
    key: string,
    title: string,
    node: React.ReactElement
  ) => (
    <CameraStatusTooltip key={key} title={title} arrow align="center">
      {node}
    </CameraStatusTooltip>
  );

  const wrapWithNameTooltip = (
    key: string,
    fullName: string,
    shortcutKey: string | null,
    node: React.ReactElement,
  ) => (
    <Tooltip
      key={key}
      title={
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{fullName}</span>
          {shortcutKey && <KeyboardCmd keys={[shortcutKey]} />}
        </Stack>
      }
      arrow
      placement="top"
    >
      {node}
    </Tooltip>
  );

  if (displayMode === 'dropdown') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Select
          value={selectorValue ?? ''}
          onChange={handleDropdownChange}
          disabled={disabled}
          displayEmpty
          size="small"
          renderValue={(selected) =>
            selected === AUTO_DIRECTOR_VALUE ? (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <Shuffle size={16} />
                <Typography variant="body2">{AUTO_DIRECTOR_CAMERA_LABEL}</Typography>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                <RedCamera />
                {typeof selected === 'number' && (
                  <Typography variant="body2">{getCameraDisplayName(selected)}</Typography>
                )}
              </Stack>
            )
          }
          sx={{
            minWidth: 'auto',
            backgroundColor: 'rgba(255, 255, 255, 0.88)',
            borderRadius: '4px',
            color: '#09090C',
            '& .MuiSelect-select': {
              py: 0.5,
              px: 1.5,
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiOutlinedInput-notchedOutline': {
              border: 'none',
            },
            '& .MuiSvgIcon-root': {
              color: '#09090C',
            },
          }}
          MenuProps={{
            PaperProps: {
              sx: {
                backgroundColor: '#1a1a1a',
                color: 'white',
              },
            },
          }}
        >
          {autoDirectorAvailable && (
            <MenuItem
              value={AUTO_DIRECTOR_VALUE}
              disabled={trackingDisabled}
              title={trackingDisabled ? AUTO_DIRECTOR_TRACKING_DISABLED_TOOLTIP : undefined}
              sx={{
                '&.Mui-disabled': {
                  opacity: 0.5,
                },
              }}
            >
              <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1}>
                <Shuffle size={16} />
                <Typography variant="body2">{AUTO_DIRECTOR_CAMERA_LABEL}</Typography>
              </Stack>
            </MenuItem>
          )}
          {availableCameras.map((camera) => {
            const camIndex = (camera as any).camIndex;
            const cameraDisabled = camera.isLive === false;
            const msg = camera.statusMessage?.trim();
            const isDegraded = !cameraDisabled && Boolean(msg);
            const fullName = getCameraDisplayName(camIndex);

            return (
              <MenuItem
                key={camera.id}
                value={camIndex}
                disabled={cameraDisabled}
                title={
                  cameraDisabled
                    ? getDisabledCameraTooltip(fullName, msg)
                    : isDegraded
                      ? msg
                      : undefined
                }
                sx={{
                  '&.Mui-disabled': {
                    opacity: 0.5,
                  },
                }}
              >
                <Stack sx={{ alignItems: 'center' }} direction="row" spacing={1}>
                  {cameraDisabled ? <GrayoutCamera /> : <CameraIcon />}
                  <Typography variant="body2">{fullName}</Typography>
                </Stack>
              </MenuItem>
            );
          })}
        </Select>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <ToggleButtonGroup
        value={selectorValue}
        exclusive
        onChange={handleSwitchCamera}
        disabled={disabled}
        aria-label="Camera selection"
        sx={{
          gap: 1,
          overflow: 'visible',
          '& .MuiToggleButton-root': {
            px: 1.5,
            py: 0.5,
            borderRadius: '4px',
            backgroundColor: 'transparent',
            color: 'white',
            border: '1px solid rgba(255, 255, 255, 0.2)',
            fontWeight: 400,
            fontSize: '12px',
            lineHeight: '16px',
            letterSpacing: '0px',
            borderWidth: 0,
            '&.Mui-selected': {
              backgroundColor: 'rgba(255, 255, 255, 0.88)',
              color: '#09090C',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255)',
              },
            },
            '&:hover:not(.Mui-selected)': {
              backgroundColor: 'rgba(255, 255, 255, 0.11)',
              '& svg path': {
                stroke: '#F04438',
              },
            },
            '&.Mui-disabled': {
              border: 'none',
              outline: 'none',
              opacity: 1,
            },
          },
        }}
      >
        {autoDirectorAvailable && (
          <Tooltip
            title={
              trackingDisabled
                ? AUTO_DIRECTOR_TRACKING_DISABLED_TOOLTIP
                : 'Auto-Director: the player picks the best camera for the tracked object'
            }
            arrow
            placement="top"
          >
            <Box sx={{ position: 'relative', display: 'inline-flex', overflow: 'visible' }}>
              <ToggleButton
                value={AUTO_DIRECTOR_VALUE}
                aria-label={AUTO_DIRECTOR_CAMERA_LABEL}
                disabled={trackingDisabled}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  sx={{
                    alignItems: 'center',
                    // The group resets `.Mui-disabled` to full opacity, so a
                    // disabled entry has to dim its own content the way the
                    // offline-camera entries below do.
                    color: trackingDisabled ? 'rgba(255, 255, 255, 0.55)' : undefined,
                  }}
                >
                  <Shuffle size={16} />
                  <Typography variant="body2">
                    {displayMode === 'abbreviated' ? 'AD' : AUTO_DIRECTOR_CAMERA_LABEL}
                  </Typography>
                </Stack>
              </ToggleButton>
            </Box>
          </Tooltip>
        )}
        {availableCameras.length > 0 ? (
          availableCameras.map((camera, index) => {
            const cameraIsAvailable = camera.isLive;
            const camIndex = (camera as any).camIndex;
            const isSelected = selectedCameraId === camIndex;
            const fullName = getCameraDisplayName(camIndex);
            const displayText = getCameraText(camIndex);
            const msg = camera.statusMessage?.trim();
            const isDegraded = cameraIsAvailable && Boolean(msg);
            const shortcutKey = index < 9 ? String(index + 1) : null;

            if (!cameraIsAvailable) {
              const disabledTooltip = getDisabledCameraTooltip(fullName, msg);
              const disabledButton = (
                <Box
                  component="span"
                  sx={{ position: 'relative', display: 'inline-flex', overflow: 'visible' }}
                >
                  <ToggleButton value={camIndex} disabled aria-label={fullName} sx={{ pr: 0.5 }}>
                    <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                      <GrayoutCamera />
                      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.55)' }}>
                        {displayText}
                      </Typography>
                    </Stack>
                  </ToggleButton>
                </Box>
              );

              return wrapWithStatusTooltip(String(camera.id), disabledTooltip, disabledButton);
            }

            const cameraIcon = isSelected ? <RedCamera /> : <CameraIcon />;
            const button = (
              <Box
                key={camera.id}
                sx={{ position: 'relative', display: 'inline-flex', overflow: 'visible' }}
              >
                <ToggleButton value={camIndex} aria-label={fullName} sx={{ overflow: 'visible' }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                    {cameraIcon}
                    <Typography variant="body2">{displayText}</Typography>
                  </Stack>
                </ToggleButton>
                {isDegraded && <CameraDegradedStatusBadge />}
              </Box>
            );

            if (isDegraded && msg) {
              return wrapWithStatusTooltip(String(camera.id), msg, button);
            }

            if (displayMode === 'abbreviated' || shortcutKey !== null) {
              return wrapWithNameTooltip(String(camera.id), fullName, shortcutKey, button);
            }

            return button;
          })
        ) : (
          <ToggleButton value={-1} disabled>
            <Typography variant="body2" color="text.secondary">
              No cameras available
            </Typography>
          </ToggleButton>
        )}
      </ToggleButtonGroup>
    </Box>
  );
};

export default PlayerCameraFeedSelectionButton;
